// src/utils/gabaritMessage.ts — SCE Issues
// La forme commune des messages que l'appli compose elle-même pour le flow 6
// (SCE_Envois) : « Envoyer au BE » (20/10) et les notifications FNC-T (F6, 23/10).
// Un mail = la même carte que l'alerte des coordinateurs (flow 1) : 600 px sur
// fond gris, bandeau jaune avec le logo des audits 5S, une ligne d'introduction,
// l'identifiant puis le titre, les faits à filets, des sections en petites
// capitales, le bouton jaune, le pied gris. Une carte Teams = le logo et
// « SCE Issues » en tête.
//
// Styles en ligne et mise en page en tableaux : Outlook ignore les feuilles de
// style et une bonne part du CSS des <div>. Couleurs écrites en clair : c'est un
// mail, pas une page de l'appli (pas de variables CSS). Pur, testable hors ligne.

/**
 * Logo Sartorius des mails (le même que ceux des audits 5S) : PNG hébergé par
 * la communication du groupe, fait pour le bandeau jaune.
 */
export const LOGO_MAIL = 'https://cdn-de1.staffbase.com/whitelabel-graphics/image/upload/v1736840497/sartorius-email-logo.png'

export const GRIS_MAIL = '#878787'
export const TEXTE_MAIL = '#3C3C3C'
export const FILET_MAIL = '#EDEDED'
const POLICE_MAIL = "'TT Norms Pro','Segoe UI',Helvetica,Arial,sans-serif"

export function echapper(t: string): string {
  return t
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/** Texte saisi → HTML : échappé, retours à la ligne gardés. */
export const enHtml = (t: string): string => echapper(t).replace(/\r?\n/g, '<br>')

export function tronquer(texte: string, longueur: number): string {
  const t = texte.trim()
  return t.length <= longueur ? t : `${t.slice(0, longueur - 1).trimEnd()}…`
}

/** Une section : titre en petites capitales grises, puis son contenu (HTML). */
export function sectionMail(titre: string, contenu: string): string {
  return (
    '<tr><td style="padding:0 32px 20px 32px">' +
    `<div style="font-size:12px;color:${GRIS_MAIL};text-transform:uppercase;letter-spacing:1px;margin-bottom:6px">${titre}</div>` +
    contenu +
    '</td></tr>'
  )
}

/** Les lignes « libellé | valeur » à filets ; `couleur` peut teinter une valeur (Critique en rouge). */
export function lignesFaits(faits: ReadonlyArray<readonly [string, string]>, couleur?: (libelle: string, valeur: string) => string): string {
  return faits
    .map(([l, v], i) => {
      const bord = i > 0 ? `border-top:1px solid ${FILET_MAIL};` : ''
      return (
        `<tr><td style="padding:6px 16px 6px 0;color:${GRIS_MAIL};width:110px;vertical-align:top;${bord}">${echapper(l)}</td>` +
        `<td style="padding:6px 0;font-weight:500;color:${couleur?.(l, v) ?? '#000000'};${bord}">${echapper(v)}</td></tr>`
      )
    })
    .join('')
}

export interface PartiesMail {
  /** Première ligne : qui fait quoi (HTML déjà échappé). */
  intro: string
  /** Au-dessus du titre, en gris : l'identifiant (P2026-0187, FNC-T_2026-58). */
  surtitre: string
  titre: string
  /** Les lignes du tableau des faits (lignesFaits). */
  faits: string
  /** Les sections (sectionMail), déjà assemblées. */
  sections: string
  lien: string
  libelleBouton: string
  /** Sous le bouton (HTML), facultatif. */
  apresBouton?: string
  /** Première ligne du pied (HTML), avant « SCE Issues — … • Pompey ». */
  pied: string
}

export function enveloppeMail(p: PartiesMail): string {
  return [
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color:#F5F5F5;padding:16px 0;font-family:${POLICE_MAIL};color:#000000">`,
    '<tr><td align="center">',
    '<table role="presentation" width="600" cellpadding="0" cellspacing="0" style="background-color:#FFFFFF;border:1px solid #DADADA;max-width:600px;width:100%">',

    // Bandeau : le logo à gauche, « SCE Issues » à droite. Le PNG a de larges
    // marges transparentes : 64 px de haut, bandeau resserré.
    '<tr><td style="background-color:#FFED00;padding:6px 32px 6px 20px">',
    '<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr>',
    `<td style="vertical-align:middle"><img src="${LOGO_MAIL}" alt="Sartorius" height="64" style="height:64px;border:0;display:block"></td>`,
    `<td style="text-align:right;vertical-align:middle;font-size:18px;line-height:22px;font-weight:700">SCE Issues</td>`,
    '</tr></table>',
    '</td></tr>',

    // Qui fait quoi : ce qu'on doit lire d'abord.
    `<tr><td style="padding:16px 32px 4px 32px;font-size:14px;color:${TEXTE_MAIL}">`,
    p.intro,
    '</td></tr>',

    // Identifiant, puis le titre
    '<tr><td style="padding:12px 32px 8px 32px">',
    `<div style="font-size:12px;color:${GRIS_MAIL}">${echapper(p.surtitre)}</div>`,
    `<div style="font-size:20px;line-height:26px;font-weight:500;margin-top:2px">${echapper(p.titre)}</div>`,
    '</td></tr>',

    // Les faits
    '<tr><td style="padding:4px 32px 16px 32px">',
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;font-size:14px">${p.faits}</table>`,
    '</td></tr>',

    p.sections,

    // Le bouton (tableau : « bouton à l'épreuve d'Outlook »)
    '<tr><td align="center" style="padding:4px 32px 24px 32px">',
    '<table role="presentation" cellpadding="0" cellspacing="0"><tr>',
    `<td style="background-color:#FFED00"><a href="${echapper(p.lien)}" style="display:inline-block;padding:12px 32px;color:#000000;font-size:15px;font-weight:600;text-decoration:none">${echapper(p.libelleBouton)}</a></td>`,
    '</tr></table>',
    p.apresBouton ?? '',
    '</td></tr>',

    `<tr><td style="padding:12px 32px;background-color:#F5F5F5;border-top:1px solid #DADADA;text-align:center;font-size:12px;line-height:18px;color:${GRIS_MAIL}">`,
    `${p.pied}<br>`,
    'SCE Issues — Sartorius Chromatography Equipment • Pompey',
    '</td></tr>',

    '</table>',
    '</td></tr>',
    '</table>',
  ].join('')
}

// ─── Carte Teams (Adaptive Card 1.4) ───

export type BlocCarte = Record<string, unknown>

export function texteCarte(text: string, extra: Record<string, unknown> = {}): BlocCarte {
  return { type: 'TextBlock', text, wrap: true, ...extra }
}

/** En tête de carte : le logo (public, Teams l'affiche) et « SCE Issues », comme la carte du flow 1. */
export function enteteCarte(): BlocCarte {
  return {
    type: 'ColumnSet',
    spacing: 'None',
    columns: [
      {
        type: 'Column',
        width: 'auto',
        verticalContentAlignment: 'Center',
        items: [{ type: 'Image', url: LOGO_MAIL, size: 'Small', altText: 'Sartorius' }],
      },
      {
        type: 'Column',
        width: 'stretch',
        verticalContentAlignment: 'Center',
        items: [texteCarte('SCE Issues', { size: 'Small', color: 'Accent', weight: 'Bolder', horizontalAlignment: 'Right', spacing: 'None' })],
      },
    ],
  }
}

/** La carte complète : un seul bouton, « Ouvrir dans SCE Issues ». */
export function carteAdaptative(corps: readonly BlocCarte[], lien: string): BlocCarte {
  return {
    type: 'AdaptiveCard',
    $schema: 'http://adaptivecards.io/schemas/adaptive-card.json',
    version: '1.4',
    body: [...corps],
    actions: [{ type: 'Action.OpenUrl', title: 'Ouvrir dans SCE Issues', url: lien, style: 'positive' }],
  }
}
